/**
 * Form Filler Service Layer
 *
 * Handles all API calls for the CaseMaker Form Filler feature.
 * Uses HTTP endpoints with Bearer token authentication.
 */

import { auth } from '../lib/firebase';
import { buildHttpHeaders } from '../utils/appCheckToken';
import { getCloudFunctionsBaseUrl } from '../utils/cloudFunctionUrl';

// ============================================================================
// TYPES
// ============================================================================

/** Field type extracted from the form */
export type FormFieldType = 'text' | 'date' | 'checkbox' | 'radio' | 'select' | 'textarea';

/** Confidence level of AI field mapping */
export type FieldConfidence = 'high' | 'medium' | 'low' | 'unmapped';

/** Single analyzed and mapped field */
export interface AnalyzedField {
  fieldId: string;
  label: string;
  section: string;
  fieldType: FormFieldType;
  existingValue?: string;
  suggestedValue: string;
  dataSource: string;
  confidence: FieldConfidence;
}

/** Request to analyze a form */
export interface AnalyzeFormRequest {
  caseFileId: string;
  formStoragePath: string;
  formFileName: string;
  userNotes?: string;
}

/** Response from form analysis */
export interface AnalyzeFormResponse {
  success: boolean;
  formAnalysisId?: string;
  formFileName?: string;
  formPageCount?: number;
  fields?: AnalyzedField[];
  unmappedCount?: number;
  totalFieldCount?: number;
  error?: string;
}

/** Single field with user's final values for form generation */
export interface ReviewedField {
  fieldId: string;
  label: string;
  finalValue: string;
  fieldType: string;
  skip: boolean;
}

/** Request to generate a filled form */
export interface GenerateFilledFormRequest {
  caseFileId: string;
  formAnalysisId: string;
  formStoragePath: string;
  fields: ReviewedField[];
  outputFileName?: string;
}

/** Response from filled form generation */
export interface GenerateFilledFormResponse {
  success: boolean;
  downloadUrl?: string;
  docId?: string;
  exportId?: string;
  expiresAt?: { seconds: number; nanoseconds: number };
  fillMethod?: 'acroform' | 'overlay' | 'companion';
  error?: string;
}

// ============================================================================
// SERVICE
// ============================================================================

/**
 * Get the current user's ID token for authentication
 */
async function getAuthToken(): Promise<string> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }
  return user.getIdToken();
}

/**
 * Make an authenticated request to a Form Filler endpoint
 */
async function makeRequest<TRequest, TResponse>(
  endpoint: string,
  method: 'POST',
  data: TRequest
): Promise<TResponse> {
  const token = await getAuthToken();
  const baseUrl = getCloudFunctionsBaseUrl();
  const url = `${baseUrl}/${endpoint}`;
  const headers = await buildHttpHeaders(token);

  const response = await fetch(url, {
    method,
    headers,
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));

    if (response.status === 429) {
      const retryAfter = errorData.retryAfterSeconds || 'a few';
      throw new Error(`You're doing a lot! Please wait ${retryAfter} seconds and try again.`);
    }

    throw new Error(errorData.error || `Request failed with status ${response.status}`);
  }

  return response.json() as Promise<TResponse>;
}

/**
 * Form Filler Service
 */
export const formFillerService = {
  /**
   * Analyze an uploaded form — extract fields and map to CaseFile data
   */
  async analyzeForm(request: AnalyzeFormRequest): Promise<AnalyzeFormResponse> {
    return makeRequest<AnalyzeFormRequest, AnalyzeFormResponse>(
      'analyzeFormHttp',
      'POST',
      request
    );
  },

  /**
   * Generate a filled PDF from reviewed field values
   */
  async generateFilledForm(request: GenerateFilledFormRequest): Promise<GenerateFilledFormResponse> {
    return makeRequest<GenerateFilledFormRequest, GenerateFilledFormResponse>(
      'generateFilledFormHttp',
      'POST',
      request
    );
  },
};

export default formFillerService;
